import getBlogImageUrl from "@Pages/Blog/utils/getBlogImageUrl";
import formatBlogPublicationDate from "@Pages/Blog/utils/formatBlogPublicationDate";
import { getBlogPostDisplayDate } from "@Pages/Blog/utils/getBlogPostDisplayDate";
import type { BlogCardPost } from "../types";

export type BlogCardPostResponse = {
  id: number;
  name: string;
  slug: string;
  mainImage?: { url?: string; alt?: string };
  category?: { name?: string };
  taglines?: string;
  metaDescription?: string;
  date?: string;
  displayDate?: string | null;
  updatedAt?: string;
  readTime?: string;
  writer?: { name?: string; slug?: string; image?: { url?: string } | null } | number | null;
};

export const BLOG_CARD_SELECT = [
  "id",
  "name",
  "slug",
  "mainImage",
  "category",
  "taglines",
  "metaDescription",
  "readTime",
  "date",
  "displayDate",
  "updatedAt",
  "writer",
]
  .map((field) => `select[${field}]=true`)
  .join("&");

export const PUBLISHED_POSTS_QUERY = `where[_status][equals]=published&where[isArchived][equals]=false&${BLOG_CARD_SELECT}&sort=-updatedAt`;

export type BlogArticlesListFilters = {
  countrySlug?: string;
  categorySlug?: string;
  stageSlug?: string;
};

const relationshipEqualsFilter = (field: string, value?: string): string =>
  value ? `where[${field}][equals]=${encodeURIComponent(value)}` : "";

/** Payload staging requires relationship `where` clauses before status/select or counts return 0. */
export const buildBlogArticlesListQuery = (
  filters: BlogArticlesListFilters = {},
  pagination: { limit?: number; page?: number } = {},
): string =>
  [
    relationshipEqualsFilter("country.slug", filters.countrySlug),
    relationshipEqualsFilter("category.slug", filters.categorySlug),
    relationshipEqualsFilter("stage", filters.stageSlug),
    PUBLISHED_POSTS_QUERY,
    pagination.limit != null ? `limit=${pagination.limit}` : "",
    pagination.page != null ? `page=${pagination.page}` : "",
  ]
    .filter(Boolean)
    .join("&");

export const formatCardDate = formatBlogPublicationDate;

export const parseReadMinutes = (readTime?: string): number | null => {
  const minutes = parseInt(readTime ?? "", 10);
  return Number.isNaN(minutes) ? null : minutes;
};

export const withImageWidth = (url: string, width: number): string => {
  if (!url) return url;
  return `${url}${url.includes("?") ? "&" : "?"}w=${width}`;
};

const toAuthor = (writer: BlogCardPostResponse["writer"]): BlogCardPost["author"] => {
  if (!writer || typeof writer !== "object" || !writer.name) return undefined;
  const photoUrl = writer.image?.url ? getBlogImageUrl(writer.image.url) : undefined;
  return { name: writer.name, slug: writer.slug, photoUrl };
};

export const toBlogCardPost = (post: BlogCardPostResponse): BlogCardPost => {
  const resolvedIso = getBlogPostDisplayDate(post) ?? "";
  return {
    id: post.id,
    slug: post.slug,
    title: post.name,
    excerpt: post.taglines?.trim() || post.metaDescription?.trim() || "",
    image: {
      url: getBlogImageUrl(post.mainImage?.url ?? ""),
      alt: post.mainImage?.alt || post.name,
    },
    category: post.category?.name ?? "",
    author: toAuthor(post.writer),
    publishedAt: resolvedIso,
    modifiedAt: resolvedIso,
    displayDate: formatCardDate(resolvedIso),
    readMinutes: parseReadMinutes(post.readTime),
  };
};
